// Satır sanallaştırma kabı (TODO-KPI-07): bağımlılıksız basit pencere. Kabın scrollTop'u ve
// yüksekliği ölçülür, `virtualWindow` (lib/kpi-grid-nav.ts — vitest) görünür aralığı + doldurucu
// yüksekliklerini verir. Kaydırmada her karede render edilmez: çapa scrollTop'tan tamponun yarısı
// (10 satır) kadar uzaklaşınca pencere kayar. ≤80 satırda `active:false` → tüm satırlar, doldurucu
// yok (DOM byte-eşit). `focusCell` render dışındaki satırı kaydırıp bir sonraki boyamada odaklar.
// Kırılım (Task 17): yükseklik satır başına dizi olabilir (`gridRowHeights` — portal grup bandı
// 40 px, satır 56 px); doldurucular ve kaydırma hedefi önek toplamlı ofsetlerden, kayma yok.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  GRID_ROW_HEIGHT,
  minRowHeight,
  rowTop,
  scrollTopForRow,
  VIRTUAL_BUFFER,
  virtualWindow,
  type VirtualWindow,
} from "../../lib/kpi-grid-nav";

/** SSR'da `useLayoutEffect` uyarı verir (sunucuda çalışmaz) — sunucuda düz `useEffect`. */
const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

export interface VirtualRowsApi {
  window: VirtualWindow;
  /** tablo kabı (`overflow:auto`) */
  containerRef: React.RefObject<HTMLDivElement>;
  /** sticky başlık (yükseklik ölçümü) */
  theadRef: React.RefObject<HTMLTableSectionElement>;
  onScroll: () => void;
  /**
   * Hücreyi odaklar; satır pencerede değilse kabı kaydırır ve odak bir sonraki render'a ertelenir.
   * `center` → satır kabın ortasına (highlight).
   */
  focusCell: (
    rowIndex: number,
    key: string,
    cellRefs: Map<string, HTMLTableCellElement>,
    opts?: { center?: boolean; preventScroll?: boolean },
  ) => void;
}

export function useVirtualRows(
  rowCount: number,
  cellRefs: React.MutableRefObject<Map<string, HTMLTableCellElement>>,
  /** sabit satır yüksekliği (portal `PORTAL_GRID_ROW_HEIGHT`) ya da satır başına dizi */
  rowHeight: number | readonly number[] = GRID_ROW_HEIGHT,
): VirtualRowsApi {
  const shiftPx = (VIRTUAL_BUFFER / 2) * minRowHeight(rowHeight);
  const containerRef = useRef<HTMLDivElement>(null);
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const [anchorTop, setAnchorTop] = useState(0);
  const [viewportH, setViewportH] = useState(0);
  const [headerH, setHeaderH] = useState(0);
  const pending = useRef<{ key: string; preventScroll: boolean } | null>(null);
  const raf = useRef(0);

  const measure = useCallback((): void => {
    const el = containerRef.current;
    if (!el) return;
    setViewportH(el.clientHeight);
    setHeaderH(theadRef.current?.offsetHeight ?? 0);
  }, []);

  useIsoLayoutEffect(() => {
    measure();
  }, [measure, rowCount]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [measure]);

  const onScroll = useCallback((): void => {
    if (raf.current) return;
    raf.current = requestAnimationFrame(() => {
      raf.current = 0;
      const el = containerRef.current;
      if (!el) return;
      const top = el.scrollTop;
      setAnchorTop((prev) => (Math.abs(top - prev) >= shiftPx ? top : prev));
    });
  }, [shiftPx]);

  const win = virtualWindow({
    rowCount,
    scrollTop: anchorTop,
    viewportHeight: viewportH,
    headerHeight: headerH,
    rowHeight,
  });

  // ertelenmiş odak: hedef hücre bu render'da DOM'a girdiyse odakla
  useEffect(() => {
    const p = pending.current;
    if (!p) return;
    const el = cellRefs.current.get(p.key);
    if (!el) return;
    pending.current = null;
    el.focus({ preventScroll: p.preventScroll });
  });

  const focusCell = useCallback<VirtualRowsApi["focusCell"]>(
    (rowIndex, key, refs, opts) => {
      const el = refs.get(key);
      const container = containerRef.current;
      if (opts?.center && container) {
        if (win.active) {
          container.scrollTop = Math.max(
            0,
            rowTop(rowIndex, rowHeight) - (container.clientHeight - headerH) / 2,
          );
        } else {
          el?.scrollIntoView({ block: "center", inline: "nearest" });
        }
      }
      if (el && !(opts?.center && win.active)) {
        el.focus({ preventScroll: opts?.preventScroll ?? false });
        return;
      }
      if (!container) return;
      if (!opts?.center) {
        container.scrollTop = scrollTopForRow({
          rowIndex,
          scrollTop: container.scrollTop,
          viewportHeight: container.clientHeight,
          headerHeight: headerH,
          rowHeight,
        });
      }
      pending.current = { key, preventScroll: true };
      // kaydırma çerçeveye kalmadan pencereyi hemen kaydır (odak aynı render döngüsünde gelsin)
      setAnchorTop(container.scrollTop);
    },
    [headerH, win.active, rowHeight],
  );

  return { window: win, containerRef, theadRef, onScroll, focusCell };
}
